"use client";

import { cn } from "@/lib/utils";
import makeSlug from "@/utils/makeSlug";
import DialogClose from "@app/_components/ui/Dialog/DialogClose";
import { useSiteInfo } from "@app/_context";
import { Button, Dialog } from "@components/ui";
import { ArrowRightIcon } from "@ngg/icons";
import { useParams } from "next/navigation";
import { ComponentProps, useMemo, useState } from "react";
import Menu, { type Menu as MenuType } from "./menu";

export function findActiveMenuItem(slug?: string, menu?: MenuType | null) {
  const sections = menu?.navigationsCollection?.items ?? [];

  const items = sections.flatMap((section) =>
    section?.sublinksCollection?.items.flatMap((item) => [
      item,
      ...(item?.sublinksCollection?.items ?? []),
    ]),
  );

  return items.find((item) => item?.internalLink?.slug === makeSlug(slug));
}

export default function SideMenu({
  menu,
  locale,
  className,
  ...props
}: ComponentProps<"nav"> & { menu?: MenuType | null; locale: string }) {
  const { dictionary } = useSiteInfo();
  const { slug } = useParams();
  const [open, setOpen] = useState(false);

  const activeMenuItem = useMemo(
    () => findActiveMenuItem(slug?.toString(), menu),
    [slug, menu],
  );

  return (
    <div className={cn("max-lg:p-3 lg:border-r", className)}>
      <Button
        className="flex w-full items-center justify-between border border-black bg-transparent px-3 py-2 lg:hidden"
        onClick={() => setOpen(true)}
      >
        {activeMenuItem?.title}
        <ArrowRightIcon className="text-lg" />
      </Button>
      <Menu
        menu={menu}
        locale={locale}
        dictionary={dictionary}
        className="max-lg:hidden"
        activeMenuItem={activeMenuItem}
        {...props}
      />
      <Dialog.Root open={open} onClose={setOpen}>
        <Dialog.Overlay />
        <Dialog.Content
          position="left"
          onClose={() => setOpen(false)}
          closeComponent={
            <DialogClose
              onClick={() => setOpen(false)}
              className="all-small-caps w-auto px-4 text-base uppercase tracking-wider underline outline-none"
            >
              {dictionary?.close}
            </DialogClose>
          }
        >
          <Dialog.Header className="border-b">
            <Dialog.Title>{dictionary?.menu}</Dialog.Title>
          </Dialog.Header>
          <Menu
            menu={menu}
            locale={locale}
            dictionary={dictionary}
            activeMenuItem={activeMenuItem}
          />
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
